import React, { useEffect, useState } from 'react';
import { Grid, Card, CardContent, Typography, Box, CircularProgress, Chip, Avatar, LinearProgress, Divider } from '@mui/material';
import { People, CalendarMonth, Inventory2, AttachMoney, TrendingUp, Warning, CheckCircle, Schedule } from '@mui/icons-material';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

const StatCard = ({ title, value, icon, color, bg, trend, subtitle }: any) => (
  <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }, transition: 'box-shadow 0.2s' }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ bgcolor: bg, p: 1.2, borderRadius: 2 }}>
          {React.cloneElement(icon, { sx: { color, fontSize: 24 } })}
        </Box>
        {trend && (
          <Chip label={`+${trend}%`} size="small" sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 700, fontSize: 11, height: 22 }} />
        )}
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>{value ?? '—'}</Typography>
      <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>{title}</Typography>
      {subtitle && <Typography variant="caption" sx={{ color: '#94a3b8' }}>{subtitle}</Typography>}
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<any>(null);
  const [todayAppts, setTodayAppts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const hospitalId = user?.hospital?._id;
        const [apptRes, statsRes] = await Promise.all([
          api.get('/appointments/today'),
          hospitalId ? api.get(`/hospitals/${hospitalId}/stats`) : Promise.resolve({ data: { data: {} } })
        ]);
        setTodayAppts(apptRes.data.data || []);
        setStats(statsRes.data.data || {});
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <CircularProgress sx={{ color: '#3b82f6' }} />
    </Box>
  );

  const statusConfig: Record<string, { color: string; bg: string; icon: any }> = {
    scheduled: { color: '#3b82f6', bg: '#eff6ff', icon: <Schedule sx={{ fontSize: 14 }} /> },
    confirmed: { color: '#10b981', bg: '#ecfdf5', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    'in-progress': { color: '#f59e0b', bg: '#fffbeb', icon: <Schedule sx={{ fontSize: 14 }} /> },
    completed: { color: '#10b981', bg: '#ecfdf5', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    cancelled: { color: '#ef4444', bg: '#fef2f2', icon: <Warning sx={{ fontSize: 14 }} /> },
    'no-show': { color: '#94a3b8', bg: '#f8fafc', icon: <Warning sx={{ fontSize: 14 }} /> },
  };

  const completed = todayAppts.filter(a => a.status === 'completed').length;
  const progress = todayAppts.length > 0 ? (completed / todayAppts.length) * 100 : 0;

  return (
    <Box>
      {/* Welcome */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
          Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 17 ? 'Afternoon' : 'Evening'}, {user?.name?.split(' ')[0]} 👋
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>Here's what's happening at your hospital today.</Typography>
      </Box>

      {/* Stat Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title="Total Patients" value={stats?.totalPatients?.toLocaleString() || 0} icon={<People />} color="#3b82f6" bg="#eff6ff" trend={12} subtitle="Active registrations" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title="Today's Appointments" value={stats?.todayAppointments || 0} icon={<CalendarMonth />} color="#10b981" bg="#ecfdf5" subtitle={`${completed} completed`} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title="Low Stock Alerts" value={stats?.lowStockItems || 0} icon={<Inventory2 />} color="#f59e0b" bg="#fffbeb" subtitle="Items need restock" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title="Total Revenue" value={`₹${((stats?.totalRevenue || 0) / 1000).toFixed(1)}K`} icon={<AttachMoney />} color="#8b5cf6" bg="#f5f3ff" trend={8} subtitle="All time collected" />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        {/* Today's Appointments */}
        <Grid item xs={12} lg={8}>
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>Today's Appointments</Typography>
                <Chip label={`${todayAppts.length} total`} size="small" sx={{ bgcolor: '#f1f5f9', color: '#64748b' }} />
              </Box>

              {todayAppts.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>{completed} of {todayAppts.length} completed</Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>{Math.round(progress)}%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: '#10b981', borderRadius: 3 } }} />
                </Box>
              )}

              {todayAppts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <CalendarMonth sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                  <Typography sx={{ color: '#94a3b8' }}>No appointments scheduled today</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {todayAppts.slice(0, 8).map(appt => {
                    const sc = statusConfig[appt.status] || statusConfig.scheduled;
                    return (
                      <Box key={appt._id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #f1f5f9', '&:hover': { bgcolor: '#f1f5f9' }, transition: 'background 0.15s' }}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: '#e0e7ff', color: '#4f46e5', fontSize: 13, fontWeight: 700 }}>
                          {appt.patient?.name?.charAt(0)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }} noWrap>{appt.patient?.name}</Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>{appt.doctor?.name || 'Unassigned'} • {appt.timeSlot}</Typography>
                        </Box>
                        <Chip
                          label={appt.status}
                          size="small"
                          icon={React.cloneElement(sc.icon, { style: { color: sc.color } })}
                          sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 600, fontSize: 11, textTransform: 'capitalize', border: `1px solid ${sc.bg}` }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Stats Panel */}
        <Grid item xs={12} lg={4}>
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', mb: 2.5 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 2 }}>Quick Stats</Typography>
              {[
                { label: 'Bed Occupancy', value: '75%', color: '#3b82f6', progress: 75 },
                { label: 'Staff on Duty', value: '24', color: '#10b981', progress: 80 },
                { label: 'Patient Satisfaction', value: '94%', color: '#8b5cf6', progress: 94 },
                { label: 'Avg. Wait Time', value: '18 min', color: '#f59e0b', progress: 60 },
              ].map(stat => (
                <Box key={stat.label} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>{stat.label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: stat.color }}>{stat.value}</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={stat.progress} sx={{ height: 5, borderRadius: 3, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: stat.color, borderRadius: 3 } }} />
                </Box>
              ))}
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 2 }}>Appointment Status</Typography>
              {Object.entries(
                todayAppts.reduce((acc: any, a) => { acc[a.status] = (acc[a.status] || 0) + 1; return acc; }, {})
              ).map(([status, count]) => {
                const sc = statusConfig[status] || statusConfig.scheduled;
                return (
                  <Box key={status} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: sc.color }} />
                      <Typography variant="body2" sx={{ color: '#64748b', textTransform: 'capitalize' }}>{status}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>{count as number}</Typography>
                  </Box>
                );
              })}
              {todayAppts.length === 0 && <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center', py: 2 }}>No data yet</Typography>}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
